import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search,
  Heart,
  User,
  ShoppingBag,
  LayoutGrid,
  HeartPulse,
  Package,
  Users,
  Info,
  ChevronRight,
  ChevronDown,
  Cross,
  X,
  Menu,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";

import { useSiteConfig } from "../context/SiteConfigContext";
import { useCart } from "../context/CartContext";
import { CategoryContext } from "../context/CategoryContext";
import Auth from "../utils/AuthUtils";

import CartCanvas from "../pages/canvas/CartCanvas";
import LoginCanvas from "../pages/canvas/LoginCanvas";
import RegisterCanvas from "../pages/canvas/RegisterCanvas";
import SearchCanvas from "../pages/canvas/SearchCanvas";


const MEGA_WIDTH = 680;
const HIDE_AFTER = 80; // itna scroll hone ke baad hi header hide hoga
const SCROLL_DELTA = 8; // chhote jitter ko ignore karne ke liye

// Category link ek hi jagah se banta hai (desktop + mobile dono).
// Agar category page slugPath ki jagah slug se chalta hai to sirf yahan badlo.
const catPath = (c) => `/categories/${c.slugPath}`;

export default function Header() {
  const { config } = useSiteConfig();
  const { cartCount } = useCart();
  const { categories = [] } = useContext(CategoryContext);
  const { pathname } = useLocation();
  const isLoggedIn = Auth.getToken();

  const [alertVisible, setAlert] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [openId, setOpenId] = useState(null); // mobile accordion (main categories)
  const [mobileCatOpen, setMobileCatOpen] = useState(false); // mobile "Categories"
  const [catOpen, setCatOpen] = useState(false); // desktop dropdown
  const [activeCatId, setActiveCatId] = useState(null); // desktop: hovered main category
  const [megaLeft, setMegaLeft] = useState(0);
  const [hidden, setHidden] = useState(false); // header scroll pe hide

  const subnavRef = useRef(null);
  const catBtnRef = useRef(null);
  const lastY = useRef(0);
  const ticking = useRef(false);

  const handleToggle = (id) => setOpenId(openId === id ? null : id);

  // Ctrl/Cmd + K -> search offcanvas open
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.getElementById("searchOffcanvas");
        if (el && window.bootstrap?.Offcanvas) {
          window.bootstrap.Offcanvas.getOrCreateInstance(el).show();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Scroll down -> header hide, scroll up -> header show
  useEffect(() => {
    lastY.current = window.scrollY;

    const update = () => {
      const y = window.scrollY;
      const diff = y - lastY.current;

      if (y < HIDE_AFTER) {
        setHidden(false); // top pe hamesha visible
        lastY.current = y;
      } else if (Math.abs(diff) >= SCROLL_DELTA) {
        setHidden(diff > 0); // neeche = hide, upar = show
        lastY.current = y;
      }
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(update);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Header hide ho to dropdown bhi band
  useEffect(() => {
    if (hidden) setCatOpen(false);
  }, [hidden]);

  // Route change par dropdown band + header wapas dikhao
  // + mobile menu offcanvas bhi band (Link pe data-bs-dismiss nahi lagaya,
  //   kyunki wo navigation rok deta tha)
  useEffect(() => {
    setCatOpen(false);
    setHidden(false);

    const el = document.getElementById("mobileMenu");
    if (el && window.bootstrap?.Offcanvas) {
      window.bootstrap.Offcanvas.getInstance(el)?.hide();
    }
  }, [pathname]);

  // Bahar click / Esc par dropdown band
  useEffect(() => {
    const onDown = (e) => {
      if (subnavRef.current && !subnavRef.current.contains(e.target)) {
        setCatOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setCatOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  if (!config) return null;

  // Nav items: sab static — "Categories" ek dropdown item hai
  const navItems = [
    { id: "home", label: "Home", path: "/", icon: <LayoutGrid size={17} /> },
    { id: "shop", label: "Shop", path: "/shop", icon: <Package size={17} /> },
    {
      id: "categories",
      label: "Categories",
      path: "/categories",
      icon: <HeartPulse size={17} />,
      dropdown: true,
    },
    { id: "about", label: "About", path: "/about-us", icon: <Info size={17} /> },
    { id: "contact", label: "Contact", path: "/contact-us", icon: <Users size={17} /> },
  ];

  const isActive = (item) =>
    item.path === "/" ? pathname === "/" : pathname.startsWith(item.path);

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  const closeMega = () => setCatOpen(false);

  // Dropdown ko Categories button ke neeche align karo
  const openMega = () => {
    if (catBtnRef.current && subnavRef.current) {
      const btn = catBtnRef.current.getBoundingClientRect();
      const wrap = subnavRef.current.getBoundingClientRect();
      const left = btn.left - wrap.left;
      const maxLeft = Math.max(0, wrap.width - Math.min(MEGA_WIDTH, wrap.width - 16));
      setMegaLeft(Math.max(0, Math.min(left, maxLeft)));
    }
    setCatOpen(true);
  };

  const toggleMega = () => (catOpen ? closeMega() : openMega());

  const currentCat =
    categories.find((c) => c._id === activeCatId) || categories[0] || null;

  // Dropdown khula ho to header hide nahi hoga
  const headerHidden = hidden && !catOpen;

  const addressText = [
    config?.address?.addressLine,
    config?.address?.district,
    config?.address?.state,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <header className={`med-header-root ${headerHidden ? "is-hidden" : ""}`}>
        {/* ── Alert Bar ── */}
        {alertVisible && config?.alertMessage && (
          <div className="med-alert-bar">
            <div className="container-fluid px-4">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <span className="alert-icon">!</span>
                  <span>{config.alertMessage}</span>
                  <span className="alert-badge">Notice</span>
                </div>
                <button
                  className="alert-dismiss"
                  onClick={() => setAlert(false)}
                  aria-label="Dismiss alert"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Main Topbar ── */}
        <nav className="med-topbar">
          <div className="container-fluid px-4 h-100">
            <div className="d-flex align-items-center justify-content-between h-100 gap-3">
              {/* Left — Logo + Brand */}
              <div className="d-flex align-items-center gap-2 gap-md-3 flex-shrink-0">
                {/* Mobile menu button */}
                <button
                  type="button"
                  className="med-icon-btn mm-trigger d-md-none"
                  aria-label="Open menu"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#mobileMenu"
                >
                  <Menu size={20} />
                </button>

                <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none">
                  <div className="med-logo-mark">
                    {config?.logo ? (
                      <img
                        src={config.logo}
                        alt="logo"
                        style={{ width: 82, height: 82, objectFit: "contain" }}
                      />
                    ) : (
                      <Cross size={18} />
                    )}
                  </div>
                  <div>
                    <div className="med-brand-name" style={{color:"#1A2333"}}>{config?.siteName || "Disha"}</div>
                    <div className="med-brand-name" style={{color:"#0A7CA5"}}>{config?.siteName || "Inc."}</div>
                  </div>
                </Link>

                <div className="med-vdivider d-none d-md-block"></div>

                <div className="med-status-pill online d-none d-md-flex">
                  <span className="dot"></span>
                  Systems Online
                </div>
              </div>

              {/* Center — Search */}
              <div className="d-none d-md-flex flex-grow-1 justify-content-center">
                <button
                  type="button"
                  className="med-search-wrap"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#searchOffcanvas"
                  aria-label="Open search"
                >
                  <Search size={16} className="med-search-icon" />
                  <span className="med-search-placeholder">
                    Search products, categories…
                  </span>
                  <span className="med-search-shortcut">
                    <kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
                    <kbd>K</kbd>
                  </span>
                </button>
              </div>

              {/* Right — Actions + User */}
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {/* Mobile search */}
                <button
                  className="med-icon-btn d-md-none"
                  aria-label="Search"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#searchOffcanvas"
                >
                  <Search size={17} />
                </button>

                {/* Cart */}
                <button
                  className="med-icon-btn"
                  aria-label="Cart"
                  title="Cart"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#shoppingCartCanvas"
                >
                  <ShoppingBag size={17} />
                  {cartCount > 0 && <span className="med-notif-count">{cartCount}</span>}
                </button>

                {/* Wishlist */}
                <Link
                  to="/wishlist"
                  className="med-icon-btn d-none d-sm-flex"
                  aria-label="Wishlist"
                  title="Wishlist"
                >
                  <Heart size={17} />
                </Link>

                <div className="med-vdivider d-none d-sm-block"></div>

                <Link
                  to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                  className="d-flex align-items-center gap-2 text-decoration-none"
                >
                  <div className="med-user-info d-none d-lg-block text-end">
                    <div className="name">{isLoggedIn ? "My Account" : "Guest"}</div>
                    <div className="role">{isLoggedIn ? "Dashboard" : "Login / Register"}</div>
                  </div>
                  <div className="med-avatar" title={isLoggedIn ? "Dashboard" : "Login"}>
                    <User size={16} />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* ── Sub Navigation (desktop only) ── */}
        <div
          className="med-subnav d-none d-md-block"
          ref={subnavRef}
          onMouseLeave={closeMega}
        >
          <div className="container-fluid px-4">
            <div className="med-subnav-scroll">
              <nav
                className="d-flex align-items-center"
                role="navigation"
                aria-label="Main navigation"
              >
                {navItems.map((item) =>
                  item.dropdown ? (
                    <button
                      key={item.id}
                      ref={catBtnRef}
                      type="button"
                      className={`med-nav-item med-nav-dropdown-btn ${
                        isActive(item) || catOpen ? "active" : ""
                      }`}
                      aria-haspopup="true"
                      aria-expanded={catOpen}
                      onMouseEnter={openMega}
                      onClick={toggleMega}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      <ChevronDown
                        size={14}
                        className={`med-nav-caret ${catOpen ? "open" : ""}`}
                      />
                    </button>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`med-nav-item ${isActive(item) ? "active" : ""}`}
                      onMouseEnter={closeMega}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  )
                )}
              </nav>
            </div>
          </div>

          {/* ── Categories Dropdown (2-pane) ── */}
          {catOpen && (
            <div
              className="med-mega"
              role="menu"
              style={{ left: megaLeft, width: MEGA_WIDTH }}
            >
              {!currentCat ? (
                <div className="med-mega-empty">No categories available</div>
              ) : (
                <>
                  {/* Left: main categories */}
                  <div className="med-mega-left">
                    <div className="med-mega-label">Browse categories</div>
                    {categories.map((cat) => (
                      <Link
                        key={cat._id}
                        to={catPath(cat)}
                        className={`med-mega-cat ${
                          currentCat._id === cat._id ? "active" : ""
                        }`}
                        onMouseEnter={() => setActiveCatId(cat._id)}
                        onFocus={() => setActiveCatId(cat._id)}
                        onClick={closeMega}
                      >
                        <span>{cat.name}</span>
                        {cat.children?.length > 0 && <ChevronRight size={14} />}
                      </Link>
                    ))}
                  </div>

                  {/* Right: sub categories of hovered category */}
                  <div className="med-mega-right">
                    <div className="med-mega-head">
                      <h6>{currentCat.name}</h6>
                      <Link
                        to={`/categories`}
                        className="med-mega-viewall"
                        onClick={closeMega}
                      >
                        View all <ChevronRight size={13} />
                      </Link>
                    </div>

                    {currentCat.children?.length > 0 ? (
                      <div className="med-mega-subgrid">
                        {currentCat.children.map((sub) => (
                          <Link
                            key={sub._id}
                            to={catPath(sub)}
                            className="med-mega-sub"
                            onClick={closeMega}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="med-mega-empty">No sub categories yet</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* SEARCH OFFCANVAS */}
      <div
        className="offcanvas offcanvas-top custom-search-offcanvas"
        id="searchOffcanvas"
        tabIndex="-1"
      >
        <div className="search-offcanvas-inner">
          <button
            className="search-close-btn"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => {
              setSearchText("");
              setShowSearchModal(false);
            }}
          >
            <X size={22} />
          </button>

          <SearchCanvas
            isOpen={showSearchModal}
            searchText={searchText}
            setSearchText={(val) => {
              setSearchText(val);
              setShowSearchModal(true);
            }}
          />
        </div>
      </div>

      {/* CART OFFCANVAS */}
      <div className="offcanvas offcanvas-end custom-cart-offcanvas" id="shoppingCartCanvas">
        <CartCanvas />
      </div>

      {/* LOGIN OFFCANVAS */}
      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="loginOffCanvas"
      >
        <LoginCanvas />
      </div>

      {/* REGISTER OFFCANVAS */}
      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="registerOffCanvas"
      >
        <RegisterCanvas />
      </div>

      {/* ───────────── MOBILE MENU OFFCANVAS (naya UI) ───────────── */}
      <div
        className="offcanvas offcanvas-start mm-offcanvas"
        id="mobileMenu"
        tabIndex="-1"
        aria-label="Mobile menu"
      >
        {/* Head */}
        <div className="mm-head">
          <Link
            to="/"
            className="mm-brand text-decoration-none"
          >
            {config?.logo ? (
              <img src={config.logo} alt="logo" className="mm-logo" />
            ) : (
              <span className="mm-logo-fallback">
                <Cross size={18} />
              </span>
            )}
            <span className="mm-brand-name">{config?.siteName || "Menu"}</span>
          </Link>

          <button
            type="button"
            className="mm-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="mm-body">
          {/* Search shortcut */}
          

          {/* Links */}
          <ul className="mm-list">
            <li>
              <Link
                to="/"
                className={`mm-link ${pathname === "/" ? "active" : ""}`}
              >
                <span className="mm-ico"><LayoutGrid size={18} /></span>
                <span className="mm-label">Home</span>
                <ChevronRight size={16} className="mm-arrow" />
              </Link>
            </li>

            <li>
              <Link
                to="/shop"
                className={`mm-link ${pathname.startsWith("/shop") ? "active" : ""}`}
              >
                <span className="mm-ico"><Package size={18} /></span>
                <span className="mm-label">Shop</span>
                <ChevronRight size={16} className="mm-arrow" />
              </Link>
            </li>

            {/* Categories accordion */}
            <li>
              <button
                type="button"
                className={`mm-link mm-toggle ${mobileCatOpen ? "open" : ""}`}
                aria-expanded={mobileCatOpen}
                onClick={() => setMobileCatOpen((v) => !v)}
              >
                <span className="mm-ico"><HeartPulse size={18} /></span>
                <span className="mm-label">Categories</span>
                <ChevronDown size={16} className="mm-caret" />
              </button>

              <div className={`mm-collapse ${mobileCatOpen ? "open" : ""}`}>
                <div className="mm-collapse-inner">
                  <ul className="mm-sublist">
                    {categories.map((cat) => {
                      const hasChildren = cat.children?.length > 0;
                      const isOpen = openId === cat._id;

                      return (
                        <li key={cat._id}>
                          {hasChildren ? (
                            <button
                              type="button"
                              className={`mm-sublink mm-subtoggle ${isOpen ? "open" : ""}`}
                              aria-expanded={isOpen}
                              onClick={() => handleToggle(cat._id)}
                            >
                              <span>{cat.name}</span>
                              <ChevronDown size={15} className="mm-caret" />
                            </button>
                          ) : (
                            <Link
                              to={catPath(cat)}
                              className="mm-sublink"
                            >
                              <span>{cat.name}</span>
                            </Link>
                          )}

                          {hasChildren && (
                            <div className={`mm-collapse ${isOpen ? "open" : ""}`}>
                              <div className="mm-collapse-inner">
                                <ul className="mm-childlist">
                                  <li>
                                    <Link
                                      to={catPath(cat)}
                                      className="mm-child mm-child-all"
                                    >
                                      All {cat.name}
                                    </Link>
                                  </li>
                                  {cat.children.map((sub) => (
                                    <li key={sub._id}>
                                      <Link
                                        to={catPath(sub)}
                                        className="mm-child"
                                      >
                                        {sub.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </li>

            <li>
              <Link
                to="/about-us"
                className={`mm-link ${pathname.startsWith("/about-us") ? "active" : ""}`}
              >
                <span className="mm-ico"><Info size={18} /></span>
                <span className="mm-label">About</span>
                <ChevronRight size={16} className="mm-arrow" />
              </Link>
            </li>

            <li>
              <Link
                to="/contact-us"
                className={`mm-link ${pathname.startsWith("/contact-us") ? "active" : ""}`}
              >
                <span className="mm-ico"><Users size={18} /></span>
                <span className="mm-label">Contact</span>
                <ChevronRight size={16} className="mm-arrow" />
              </Link>
            </li>

            <li>
              <Link
                to="/wishlist"
                className={`mm-link ${pathname.startsWith("/wishlist") ? "active" : ""}`}
              >
                <span className="mm-ico"><Heart size={18} /></span>
                <span className="mm-label">Wishlist</span>
                <ChevronRight size={16} className="mm-arrow" />
              </Link>
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mm-foot">
          <Link
            to={isLoggedIn ? "/users/dashboard" : "/users/login"}
            className="mm-cta"
          >
            <User size={16} />
            {isLoggedIn ? "My Dashboard" : "Login / Register"}
          </Link>

          <div className="mm-contact">
            {addressText && (
              <div className="mm-contact-row">
                <MapPin size={14} />
                <span>
                  {addressText}
                  {config?.address?.pincode ? ` - ${config.address.pincode}` : ""}
                </span>
              </div>
            )}
            {config?.email && (
              <a className="mm-contact-row" href={`mailto:${config.email}`}>
                <Mail size={14} />
                <span>{config.email}</span>
              </a>
            )}
            {config?.phone && (
              <a className="mm-contact-row" href={`tel:${config.phone}`}>
                <Phone size={14} />
                <span>+91-{config.phone}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}